import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './MarketCard.css';
import { MarketHeader } from './MarketHeader';
import { AmountSelector } from './AmountSelector';
import { PositionSection } from './PositionSection';
import { OptionSection } from './OptionSection';
import { TradeToggle, TradeMode } from './TradeToggle';
import { ExpectedPositionData } from './types';
import { useMarket } from '../../contexts/MarketProvider';
import { useWallet } from '../../contexts/WalletProvider';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { 
  calculateExpectedPosition,
  calculateDoubleDownPosition,
  calculateProjectedWin
} from '../../utils/bondingCurve';
import { optionToId, displayQuestion } from '../../utils/displayText';

// ============================================================================
// MAIN MARKET CARD COMPONENT
// ============================================================================

const MarketCard: React.FC = () => {
  // Get market address from URL params
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();
  
  // Get wallet connection state
  const { isAuthenticated, login, canAfford } = useWallet();
  
  // Get market data and actions from context
  const { 
    selectedMarket,
    isLoadingMarket,
    error: contextError,
    selectMarket,
    clearSelectedMarket,
    buyTokens,
    sellTokens,
  } = useMarket();
  
  // Track if we've loaded the initial market data
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  
  // Local error state for display (can be dismissed)
  const [displayError, setDisplayError] = useState<string | null>(null);
  
  // Share button state
  const [copied, setCopied] = useState(false);
  
  // Trade mode state
  const [tradeMode, setTradeMode] = useState<TradeMode>('buy');
  
  // User selection state
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null); // No default - user must select
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [hasVoted, setHasVoted] = useState<boolean>(false); // Track if vote has been cast
  
  // Post-vote state - track the original voted option and amount
  const [votedOption, setVotedOption] = useState<string | null>(null);
  const [votedAmount, setVotedAmount] = useState<number>(0);
  const [currentSellValue, setCurrentSellValue] = useState<number | null>(null);
  const [projectedWin, setProjectedWin] = useState<number | null>(null);
  const [projectedMultiplier, setProjectedMultiplier] = useState<number | null>(null);
  
  // Post-vote interaction state
  const [postVoteAmount, setPostVoteAmount] = useState<number | null>(null); // Amount selected after voting
  const [sellPercentage, setSellPercentage] = useState<number | null>(null); // Percentage to sell (10, 50, 100)
  
  // ✅ Local loading states for instant UI feedback (bypasses context delays)
  const [isCastingVote, setIsCastingVote] = useState(false);
  const [isDoublingDown, setIsDoublingDown] = useState(false);
  const [isSelling, setIsSelling] = useState(false);

  // Action button ready states based on trade mode
  // BUY mode: 
  //   - No position: Ready when option + amount selected + can afford
  //   - Has position: Ready when amount selected + can afford (option locked to current)
  // SELL mode:
  //   - Has position: Ready when percentage selected
  const isActionReady = 
    (tradeMode === 'buy' && (
      (!hasVoted && selectedOption !== null && selectedAmount !== null && canAfford(selectedAmount)) ||
      (hasVoted && postVoteAmount !== null && canAfford(postVoteAmount))
    )) ||
    (tradeMode === 'sell' && hasVoted && sellPercentage !== null);
  
  // Check if market has ended (computed early so handlers/effects can use it)
  const isMarketEnded = selectedMarket ? new Date(selectedMarket.ends_at).getTime() <= Date.now() : false;

  // Load market when address changes
  useEffect(() => {
    if (address) {
      selectMarket(address);
    }
    
    // Clear selected market when leaving the page
    return () => {
      clearSelectedMarket();
    };
  }, [address, selectMarket, clearSelectedMarket]);

  // Mark as loaded once we have market data
  useEffect(() => {
    if (selectedMarket && !hasLoadedInitialData) {
      setHasLoadedInitialData(true);
    }
  }, [selectedMarket, hasLoadedInitialData]);
  
  // Handle back navigation
  const handleBack = () => {
    navigate('/');
  };

  // Handle share URL copy
  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Auto-dismiss error after 2 seconds
  React.useEffect(() => {
    if (contextError) {
      setDisplayError(contextError);
      const timer = setTimeout(() => {
        setDisplayError(null);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [contextError]);

  // Sync local state with on-chain position whenever it changes
  useEffect(() => {
    if (!selectedMarket?.userPosition) {
      // No position - reset to initial state
      if (hasVoted) {
        setHasVoted(false);
        setVotedOption(null);
        setSelectedOption(null);
        setVotedAmount(0);
        setCurrentSellValue(null);
        setProjectedWin(null);
        setProjectedMultiplier(null);
        // Reset to buy mode if user was in sell mode
        if (tradeMode === 'sell') {
          setTradeMode('buy');
        }
      }
      return;
    }
    
    const userPosition = selectedMarket.userPosition;
    const optionLabel = selectedMarket.options[userPosition.optionIndex];
    const optionId = optionToId(optionLabel);
    
    // Calculate current sell value and projected win
    let sellValue: number | null = null;
    let projWin: number | null = null;
    let projMult: number | null = null;
    
    if (selectedMarket.onChainState) {
      const optionState = selectedMarket.onChainState.options.find(
        o => o.optionIndex === userPosition.optionIndex
      );
      
      if (optionState && optionState.totalTokens > 0) {
        // Current sell value depends on market state:
        // - Ended market (claiming): (userTokens / totalTokens) × totalPool (all options)
        // - Active market (early exit): (userTokens / totalTokens) × optionDeposits
        const tokenPercentage = userPosition.tokens / optionState.totalTokens;
        if (isMarketEnded) {
          sellValue = (tokenPercentage * selectedMarket.onChainState.totalDeposits) / LAMPORTS_PER_SOL;
        } else {
          sellValue = (tokenPercentage * optionState.totalDeposits) / LAMPORTS_PER_SOL;
        }
        
        // Calculate projected win
        // Get the other option's deposits (assuming 2 options for now)
        const otherOptionState = selectedMarket.onChainState.options.find(
          o => o.optionIndex !== userPosition.optionIndex
        );
        const otherOptionDeposits = otherOptionState?.totalDeposits || 0;
        
        // Calculate totals
        const totalVirtualLiquidity = selectedMarket.onChainState.options.reduce(
          (sum, opt) => sum + opt.virtualLiquidity, 0
        );
        const totalRealDeposits = selectedMarket.onChainState.options.reduce(
          (sum, opt) => sum + opt.totalDeposits, 0
        );
        
        const projectedWinResult = calculateProjectedWin(
          userPosition.tokens,
          userPosition.costBasis,
          optionState.totalDeposits,
          optionState.totalTokens,
          optionState.virtualLiquidity,
          otherOptionDeposits,
          totalVirtualLiquidity,
          totalRealDeposits
        );
        
        projWin = projectedWinResult.projectedWin;
        projMult = projectedWinResult.multiplier;
      }
    }
    
    // Update local state to match on-chain position
    setHasVoted(true);
    setVotedOption(optionId);
    setSelectedOption(optionId);
    setVotedAmount(userPosition.costBasis / LAMPORTS_PER_SOL);
    setCurrentSellValue(sellValue);
    setProjectedWin(projWin);
    setProjectedMultiplier(projMult);
  }, [selectedMarket?.userPosition, selectedMarket?.options, selectedMarket?.onChainState, hasVoted, tradeMode, isMarketEnded]);

  // Reset mode-specific state when trade mode changes
  useEffect(() => {
    // Clear all mode-specific selections when switching modes
    setPostVoteAmount(null);
    setSellPercentage(null);
    
    // If switching to BUY mode and no vote yet, clear amount selection
    if (tradeMode === 'buy' && !hasVoted) {
      setSelectedAmount(null);
    }
  }, [tradeMode, hasVoted]);

  // Handle trade mode toggle with validation
  const handleTradeModeToggle = (mode: TradeMode) => {
    // Prevent switching to sell mode without a position
    if (mode === 'sell' && !hasVoted) {
      return;
    }
    // Prevent switching to buy mode on ended markets
    if (mode === 'buy' && isMarketEnded) {
      return;
    }
    setTradeMode(mode);
  };
  
  // Auto-switch to sell mode when market has ended and user has a position
  useEffect(() => {
    if (isMarketEnded && hasVoted && tradeMode !== 'sell') {
      setTradeMode('sell');
    }
  }, [isMarketEnded, hasVoted, tradeMode]);

  // Transform market data for UI
  const marketData = useMemo(() => {
    if (!selectedMarket) return null;
    
    // Use on-chain state if available, otherwise use empty data
    const onChainState = selectedMarket.onChainState;
    
    const winningOptionIndex = selectedMarket.outcome?.winning_option_index ?? null;

    return {
      id: selectedMarket.id,
      question: displayQuestion(selectedMarket.question),
      rawQuestion: selectedMarket.question, // Full question for tooltip
      imageUrl: selectedMarket.image_url,
      endsAt: new Date(selectedMarket.ends_at),
      options: selectedMarket.options.map((optionLabel, idx) => {
        // Get on-chain data for this option if available
        const onChainOption = onChainState?.options.find(o => o.optionIndex === idx);
        
        return {
          id: optionToId(optionLabel),
          label: optionLabel, // Raw label - display components apply formatting
          votes: onChainOption ? onChainOption.totalDeposits / LAMPORTS_PER_SOL : 0,
          percentage: onChainOption?.percentage || 0,
          virtualLiquidity: onChainOption?.virtualLiquidity || 0,
          totalTokens: onChainOption?.totalTokens || 0,
          isLeading: onChainOption?.isLeading || false,
          isWinner: winningOptionIndex !== null ? idx === winningOptionIndex : undefined
      };
    })
    };
  }, [selectedMarket]);

  // Calculate expected position based on current selections
  const expectedPositionData: ExpectedPositionData | null = useMemo(() => {
    if (!marketData || !selectedMarket?.onChainState) return null;
    
    // Helper function to calculate expected SOL from selling
    const calculateSellReturn = (percentage: number): number | null => {
      if (!selectedMarket?.userPosition || !selectedMarket?.onChainState) return null;
      
      const userPosition = selectedMarket.userPosition;
      const tokensToSell = (userPosition.tokens * percentage) / 100;
      
      // Find the option state
      const optionState = selectedMarket.onChainState.options.find(
        o => o.optionIndex === userPosition.optionIndex
      );
      
      if (!optionState || optionState.totalTokens === 0) return null;
      
      const tokenPercentage = tokensToSell / optionState.totalTokens;
      
      // For ended markets (claiming winnings), payout comes from the total pool across all options.
      // For active markets (early exit), payout comes from only the option's deposits (bonding curve).
      if (isMarketEnded) {
        const totalPool = selectedMarket.onChainState.totalDeposits;
        return (tokenPercentage * totalPool) / LAMPORTS_PER_SOL;
      }
      
      // Active market: (tokens / total_tokens) × option.total_deposits
      const solFromSale = (tokenPercentage * optionState.totalDeposits) / LAMPORTS_PER_SOL;
      return solFromSale;
    };
    
    // Calculate totals across ALL options (needed for share calculations)
    const totalVirtualLiquidity = marketData.options.reduce(
      (sum, opt) => sum + opt.virtualLiquidity, 0
    );
    const totalRealDeposits = marketData.options.reduce(
      (sum, opt) => sum + (opt.votes * LAMPORTS_PER_SOL), 0
    );
    
    // CASE 1: Before voting - calculate expected position for new vote
    if (!hasVoted && selectedOption && selectedAmount) {
      const option = marketData.options.find(o => o.id === selectedOption);
      // Only check virtual liquidity (totalShares can be 0 for first deposit)
      if (!option || option.virtualLiquidity === 0) return null;
      
      const depositLamports = selectedAmount * LAMPORTS_PER_SOL;
      
      // First, calculate tokens user will receive
      const expected = calculateExpectedPosition(
        depositLamports,
        option.virtualLiquidity,
        option.votes * LAMPORTS_PER_SOL,
        totalVirtualLiquidity,
        totalRealDeposits,
        option.totalTokens // Pass current total tokens in the pool
      );
      
      // Now calculate projected win using the advanced formula
      // We need to simulate the state AFTER the user's deposit
      const userOptionDepositsAfter = (option.votes * LAMPORTS_PER_SOL) + depositLamports;
      const userOptionTotalTokensAfter = option.totalTokens + expected.expectedTokens;
      const totalRealDepositsAfter = totalRealDeposits + depositLamports;
      
      // Get other option's deposits (for 2-option markets)
      const otherOption = marketData.options.find(o => o.id !== selectedOption);
      const otherOptionDeposits = otherOption ? otherOption.votes * LAMPORTS_PER_SOL : 0;
      
      // Calculate projected win with winning/losing scenario logic
      const projectedWinResult = calculateProjectedWin(
        expected.expectedTokens,
        depositLamports,
        userOptionDepositsAfter,
        userOptionTotalTokensAfter,
        option.virtualLiquidity,
        otherOptionDeposits,
        totalVirtualLiquidity,
        totalRealDepositsAfter
      );
      
      return {
        type: 'new_vote' as const,
        expectedTokens: expected.expectedTokens,
        expectedValue: projectedWinResult.projectedWin,
        expectedMultiplier: projectedWinResult.multiplier,
        depositAmount: selectedAmount
      };
    }
    
    // CASE 2: After voting - calculate double down
    if (hasVoted && postVoteAmount && selectedMarket.userPosition) {
      const option = marketData.options.find(o => o.id === votedOption);
      if (!option || option.virtualLiquidity === 0 || option.totalTokens === 0) return null;
      
      const additionalLamports = postVoteAmount * LAMPORTS_PER_SOL;
      
      const doubleDown = calculateDoubleDownPosition(
        selectedMarket.userPosition.tokens,
        selectedMarket.userPosition.costBasis,
        additionalLamports,
        option.virtualLiquidity,
        option.votes * LAMPORTS_PER_SOL,
        option.totalTokens,
        totalVirtualLiquidity,
        totalRealDeposits
      );
      
      // Get other option's deposits (for 2-option markets)
      const otherOption = marketData.options.find(o => o.id !== votedOption);
      const otherOptionDeposits = otherOption ? otherOption.votes * LAMPORTS_PER_SOL : 0;
      
      // Calculate projected win using advanced formula (accounts for winning/losing scenarios)
      // State AFTER the additional deposit
      const userOptionDepositsAfter = (option.votes * LAMPORTS_PER_SOL) + additionalLamports;
      const userOptionTotalTokensAfter = option.totalTokens + doubleDown.additionalTokens;
      const totalRealDepositsAfter = totalRealDeposits + additionalLamports;
      const newCostBasisLamports = selectedMarket.userPosition.costBasis + additionalLamports;
      
      const projectedWinResult = calculateProjectedWin(
        doubleDown.newTokens,
        newCostBasisLamports,
        userOptionDepositsAfter,
        userOptionTotalTokensAfter,
        option.virtualLiquidity,
        otherOptionDeposits,
        totalVirtualLiquidity,
        totalRealDepositsAfter
      );
      
      return {
        type: 'double_down' as const,
        currentValue: projectedWin ?? undefined, // Current projected win (already calculated in component state)
        currentMultiplier: projectedMultiplier ?? undefined,
        newValue: projectedWinResult.projectedWin,
        newMultiplier: projectedWinResult.multiplier,
        additionalTokens: doubleDown.additionalTokens,
        depositAmount: postVoteAmount,
        expectedChange: projectedWinResult.projectedWin - (projectedWin || 0),
        currentTotalDeposited: selectedMarket.userPosition.costBasis / LAMPORTS_PER_SOL,
        newTotalDeposited: doubleDown.newCostBasis
      };
    }
    
    // CASE 3: After voting - calculate sell preview
    if (hasVoted && sellPercentage && selectedMarket.userPosition) {
      const option = marketData.options.find(o => o.id === votedOption);
      if (!option) return null;
      
      const expectedSolReturn = calculateSellReturn(sellPercentage);
      if (expectedSolReturn === null) return null;
      
      return {
        type: 'sell' as const,
        sellPercentage,
        expectedSolReturn
      };
    }
    
    return null;
  }, [marketData, selectedMarket, hasVoted, selectedOption, selectedAmount, postVoteAmount, votedOption, sellPercentage, projectedWin, projectedMultiplier, isMarketEnded]);

  const handleAmountSelect = (amount: number) => {
    // In SELL mode, handle percentage selection
    if (tradeMode === 'sell') {
      // 0 = reset signal from input/reset button
      setSellPercentage(amount === 0 ? null : amount);
      return;
    }
    
    if (!hasVoted) {
      // Before voting - set amount (0 = reset/clear)
      setSelectedAmount(amount === 0 ? null : amount);
    } else {
      // After voting in BUY mode - set amount for adding more (0 = reset/clear)
      setPostVoteAmount(amount === 0 ? null : amount);
    }
  };

  const handleOptionSelect = (optionId: string) => {
    // After voting, options are locked (must sell first to change position)
    if (hasVoted) {
      return; // Can't change option after voting - sell first, then buy new option
    }
    
    // Before voting - toggle selection
    if (selectedOption === optionId) {
      // Clicking same option again - deselect
      setSelectedOption(null);
    } else {
      setSelectedOption(optionId);
    }
  };

  const handleAction = async () => {
    // Check wallet connection first
    if (!isAuthenticated) {
      login();
      return;
    }
    
    if (tradeMode === 'buy') {
      // BUY MODE: Share Opinion or Add More
      if (!hasVoted) {
        // Initial vote - need option + amount
        if (!selectedOption || !selectedAmount) return;
        
        // Check if user can afford
        if (!canAfford(selectedAmount)) {
          setDisplayError(`Insufficient SOL`);
          return;
        }
        
        if (isCastingVote || !marketData) return;
        setIsCastingVote(true);
        
        // Find the option index from the selected option ID
        const optionIndex = marketData.options.findIndex(opt => opt.id === selectedOption);
        if (optionIndex === -1) {
          console.error('Invalid option selected');
          setIsCastingVote(false);
          return;
        }
        
        // Call on-chain transaction (errors handled in context)
        await buyTokens(optionIndex, selectedAmount);
        setIsCastingVote(false);
        
      } else {
        // Add More - only need amount (option is locked)
        if (!postVoteAmount) return;
        
        // Check if user can afford
        if (!canAfford(postVoteAmount)) {
          setDisplayError(`Insufficient SOL`);
          return;
        }
        
        if (isDoublingDown || !marketData) return;
        setIsDoublingDown(true);
        
        // Find the option index from the voted option
        const optionIndex = marketData.options.findIndex(opt => opt.id === votedOption);
        if (optionIndex === -1) {
          console.error('Invalid option');
          setIsDoublingDown(false);
          return;
        }
        
        // Call on-chain transaction to add more to existing position
        await buyTokens(optionIndex, postVoteAmount);
        
        // Reset post-vote amount
        setPostVoteAmount(null);
        setIsDoublingDown(false);
      }
    } else if (tradeMode === 'sell') {
      // SELL MODE: Sell percentage of position
      if (!hasVoted || !sellPercentage || !selectedMarket?.userPosition) return;
      
      if (isSelling || !marketData) return;
      setIsSelling(true);
      
      // Calculate tokens to sell
      const tokensToSell = (selectedMarket.userPosition.tokens * sellPercentage) / 100;
      
      if (tokensToSell <= 0) {
        setDisplayError('No tokens to sell');
        setIsSelling(false);
        return;
      }
      
      // Call on-chain transaction to sell tokens
      await sellTokens(tokensToSell);
      
      setSellPercentage(null);
      setIsSelling(false);
    }
  };
  
  
  // Determine what to show
  const isLoading = isLoadingMarket || (!hasLoadedInitialData && !selectedMarket);
  const hasNoMarket = !selectedMarket && !isLoadingMarket && hasLoadedInitialData;
  const hasMarketData = !!marketData;

  return (
    <div className="market-card-container">
      {/* Navigation Row - Back and Share */}
      <div className="market-card-nav-row">
        <button className="market-card-back-button" onClick={handleBack}>
          ← Back to Markets
        </button>
        <button className={`market-card-share-button ${copied ? 'copied' : ''}`} onClick={handleShare}>
          {copied ? (
            <>✓ Copied</>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              Share
            </>
          )}
        </button>
      </div>
      
      <div className="market-card">
        {/* Show loading, no market, or market content */}
        {isLoading ? (
          <div className="market-loading">
            <p>Loading market...</p>
          </div>
        ) : hasNoMarket ? (
          <div className="market-loading">
            <p>Market not found</p>
            <button className="market-back-link" onClick={handleBack}>
              Browse markets
            </button>
          </div>
        ) : hasMarketData ? (
          <>
            {/* Combined Header + Hero Section */}
            <MarketHeader 
              question={marketData.question}
              rawQuestion={marketData.rawQuestion}
              imageUrl={marketData.imageUrl}
              endsAt={marketData.endsAt}
              totalDeposits={selectedMarket?.onChainState ? selectedMarket.onChainState.totalDeposits / LAMPORTS_PER_SOL : 0}
              totalParticipants={selectedMarket?.onChainState?.totalParticipants || 0}
              outcome={selectedMarket?.outcome}
            />

            {/* Trade Toggle - Buy/Sell/Switch */}
            <TradeToggle
              activeMode={tradeMode}
              onToggle={handleTradeModeToggle}
              hasPosition={hasVoted}
              disabled={isMarketEnded && !hasVoted}
              disableBuy={isMarketEnded && hasVoted}
            />

            {/* Options Section */}
            <OptionSection 
              options={marketData.options}
              selectedOption={hasVoted ? votedOption : selectedOption}
              onOptionSelect={handleOptionSelect}
              disabled={isMarketEnded || hasVoted || tradeMode === 'sell'}
            />

            {/* Amount Selection Pills - Below Options */}
            <AmountSelector
              selectedAmount={tradeMode === 'sell' ? sellPercentage : (hasVoted ? postVoteAmount : selectedAmount)}
              onAmountSelect={handleAmountSelect}
              disabled={isMarketEnded && !hasVoted}
              isDoubleDownMode={hasVoted && postVoteAmount !== null}
              isSellMode={tradeMode === 'sell'}
            />

            {/* Position Section - Above Action Buttons */}
            <PositionSection
              selectedAmount={hasVoted ? votedAmount : selectedAmount}
              selectedOption={hasVoted ? votedOption : selectedOption}
              costBasis={hasVoted ? votedAmount : null}
              currentSellValue={hasVoted ? currentSellValue : null}
              projectedWin={hasVoted ? projectedWin : null}
              projectedMultiplier={hasVoted ? projectedMultiplier : null}
              hasVoted={hasVoted}
              expectedPositionData={expectedPositionData}
              postVoteAmount={postVoteAmount}
            />

            {/* Action Buttons with Error Overlay */}
            <div className="action-buttons-wrapper">
              {/* Error notification - toast overlay over buttons */}
              {displayError && (
                <div className="error-notification">
                  <span>{displayError}</span>
                  <button onClick={() => setDisplayError(null)}>×</button>
                </div>
              )}
              
              <div className="action-buttons-container">
              {isMarketEnded && !hasVoted ? (
                <button 
                  className="action-button cast-vote-button"
                  disabled
                >
                  Market Ended
                </button>
              ) : !isAuthenticated ? (
                <button 
                  className="action-button cast-vote-button active"
                  onClick={handleAction}
                >
                  Connect Wallet
                </button>
              ) : (
                <button 
                  className={`action-button cast-vote-button ${isActionReady ? 'active' : ''}`}
                  onClick={handleAction}
                  disabled={!isActionReady || isCastingVote || isDoublingDown || isSelling}
                >
                  {(isCastingVote || isDoublingDown || isSelling) ? (
                    <span className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  ) : tradeMode === 'buy' ? (
                    !hasVoted ? (
                      selectedOption && selectedAmount && !canAfford(selectedAmount) ? 
                        `Insufficient SOL` : 
                        'Share Opinion'
                    ) : (
                      postVoteAmount && !canAfford(postVoteAmount) ? 
                        `Insufficient SOL` : 
                        'Add More'
                    )
                  ) : tradeMode === 'sell' ? (
                    'Sell'
                  ) : (
                    'Action'
                  )}
                </button>
              )}
              </div>
              
              {/* Rules explanation */}
              <div className="market-rules-text">
                Highest SOL-weighted opinion wins
              </div>
            </div>
          </>
        ) : (
          <div className="market-loading">
            <p>Market not found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketCard;
